import { useEffect, useState, SetStateAction, Dispatch, useRef } from "react";
import { Button, Form } from "react-bootstrap";
import { ReactComponent as OutlineCopy } from "assets/svg/outline-copy.svg";

// services
import getICalExportToken from "services/icalendar/getICalExportToken";
import resetICalExportToken from "services/icalendar/resetICalExportToken";
// styles
import styles from "./styles.module.scss";
// components
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import IconButton from "components/icon-button";
import SuccessTag from "../../../../../../../../success-tag/SuccessTag";
import messages from "messages";
import { AmplitudePageName, track } from "utils/amplitude";

const ANALYTICS_COMPONENT_NAME = "sync-with-external-cals-modal";

type Props = {
  showConnectICalendar: boolean;
  setShowConnectICalendar: Dispatch<SetStateAction<boolean>>;
  analyticsPageName: AmplitudePageName;
};

/**
 * renders a button to sync or unsync outlook and iCalendar
 */
const ICalCalendar = ({
  showConnectICalendar,
  setShowConnectICalendar,
  analyticsPageName,
}: Props) => {
  const [busy, setBusy] = useState(false);
  const [iCalLink, setICalLink] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [error, setError] = useState<any>();

  const copyLinkTaget = useRef(null);
  const resetDoneTarget = useRef(null);

  useEffect(() => {
    setBusy(true);
    getICalExportToken()
      .then((res) => {
        const { ical_sync_link } = res;
        setICalLink(ical_sync_link);
      })
      .catch(setError)
      .finally(() => setBusy(false));
  }, []);

  const resetICal = async () => {
    setBusy(true);
    try {
      const response = await resetICalExportToken();
      const { ical_sync_link } = response;
      setICalLink(ical_sync_link);
      setResetDone(true);
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!iCalLink) return;
    try {
      await navigator.clipboard.writeText(iCalLink);
      setLinkCopied(true);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <>
      <SuccessTag
        target={copyLinkTaget.current}
        show={linkCopied}
        onHide={() => setLinkCopied(false)}
        placement={"top-end"}
        successMsg={"Copied to Clipoard"}
        duration={1000}
      />
      <SuccessTag
        target={resetDoneTarget.current}
        show={resetDone}
        onHide={() => setResetDone(false)}
        placement={"right"}
        successMsg={"Address reset"}
        duration={1000}
      />
      <ModalLayout
        show={showConnectICalendar}
        onHide={() => {
          track(
            analyticsPageName,
            [ANALYTICS_COMPONENT_NAME, "close-modal-button"],
            "click",
          );
          setShowConnectICalendar(false);
        }}
        Header={"Sync with External Calendars"}
        contentClassName={styles["connect-modal__content"]}
        headerClass={styles["connect-modal__header"]}
        bodyClass={styles["connect-modal__body"]}
        Footer={
          <Button
            variant="outline-primary"
            onClick={() => {
              track(
                analyticsPageName,
                [ANALYTICS_COMPONENT_NAME, "close-modal-button"],
                "click",
              );
              setShowConnectICalendar(false);
            }}
          >
            {messages.close}
          </Button>
        }
        footerClass={styles["connect-modal__footer"]}
        disabled={busy}
        error={error}
      >
        <div className={"bd-16 mb-4"}>Outlook & iCal</div>
        1-way Sync allow you to view Orchid sessions in an external calendar.
        All changes to your patient sessions must be made directly in Orchid.
        <br />
        <br />
        <div className={"mb-4"}>
          <span className={"red"}>Warning:</span> You should not give this
          address to other people.
        </div>
        <div className={"d-flex align-items-center"}>
          <Form.Control
            type="text"
            value={iCalLink || ""}
            onChange={() => null}
          />
          <span className={"ml-2"}>
            <IconButton ref={copyLinkTaget}>
              <OutlineCopy
                onClick={() => {
                  track(
                    analyticsPageName,
                    [ANALYTICS_COMPONENT_NAME, "copy-link-button"],
                    "click",
                  );
                  copyLink();
                }}
              />
            </IconButton>
          </span>
        </div>
        <hr />
        <div className={"mb-2"}>
          You can reset this address and make the current one invalid.
        </div>
        <Button
          ref={resetDoneTarget}
          variant={"outline-primary"}
          className={"mb-4"}
          onClick={() => {
            track(
              analyticsPageName,
              [ANALYTICS_COMPONENT_NAME, "reset-button"],
              "click",
            );
            resetICal();
          }}
        >
          Reset
        </Button>
        <div className={""}>
          Need help? Read our{" "}
          <a
            href="https://help.orchid.exchange/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              track(
                analyticsPageName,
                [ANALYTICS_COMPONENT_NAME, "knowledge-base-link"],
                "click",
              )
            }
          >
            Knowledge Base for Outlook or iCal
          </a>
          .
        </div>
      </ModalLayout>
    </>
  );
};

export default ICalCalendar;
